import ArrowBack from "@mui/icons-material/ArrowBack";
import Search from "@mui/icons-material/Search";
import { Box, IconButton, InputAdornment, Stack, Tooltip } from "@mui/material";
import { TextInput } from "components/TextInput";
import { useEffect, useRef } from "react";

import { ScoutThreadListSection, useScoutThreadsSearch, type ScoutThreadListItem } from "../Search";

/**
 * In-popover "chat history" view. Mirrors the full-page side panel's search +
 * recents experience (same `useScoutThreadsSearch` hook and list component) but
 * laid out for the narrow popover: a single list that shows recents when the
 * query is empty and search results as the user types. Selecting a thread loads
 * it into the popover rather than navigating to the full page.
 */
export function PopoverScoutSearch({
  onSelectThread,
  onBack,
}: {
  onSelectThread: (thread: ScoutThreadListItem) => void;
  onBack: () => void;
}) {
  const {
    searchInput,
    setSearchInput,
    heading,
    items,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    emptyMessage,
    errorMessage,
  } = useScoutThreadsSearch({ enabled: true, fetchPolicy: "cache-and-network" });

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <Stack sx={{ flex: 1, minHeight: 0, px: 2, gap: 1.5 }}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Tooltip title="Back to chat">
          <IconButton size="small" onClick={onBack} aria-label="Back to chat">
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <TextInput
          placeholder="Search chats"
          fullWidth
          inputRef={inputRef}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          }
        />
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <ScoutThreadListSection
          heading={heading}
          items={items}
          loading={isInitialLoading}
          hasMore={hasMore}
          loadingMore={isLoadingMore}
          emptyMessage={emptyMessage}
          errorMessage={errorMessage}
          onLoadMore={loadMore}
          onSelectItem={onSelectThread}
          compact
        />
      </Box>
    </Stack>
  );
}
